import { type VariantProps, cva } from "class-variance-authority";
import { cx } from "class-variance-authority";
import * as React from "react";

import { Pacman } from "../Pacman/Pacman";
import { Skeleton } from "../Skeleton/Skeleton";

import * as styles from "./Button.module.scss";

const buttonVariants = cva(styles.button, {
  variants: {
    variant: {
      primary: styles.primary,
      secondary: styles.secondary,
      outlined: styles.outlined,
      blurred: styles.blurred,
      danger: styles.danger,
      warning: styles.warning,
      info: styles.info,
      link: styles.link,
      noStyle: styles.noStyle,
    },
    size: {
      default: styles.sizeDefault,
      md: styles.sizeMd,
      sm: styles.sizeSm,
      xs: styles.sizeXs,
      xxs: styles.sizeXxs,
    },
  },
  defaultVariants: {
    variant: "primary",
    size: "default",
  },
});

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & {
    icon?: React.ReactNode;
    loading?: boolean;
    skeleton?: boolean;
    gutterTop?: boolean;
  };

const Button = ({
  ref,
  className,
  gutterTop,
  variant,
  size,
  icon,
  children,
  loading,
  skeleton = false,
  ...props
}: ButtonProps & {
  ref?: React.Ref<HTMLButtonElement>;
}) => {
  const iconComponent = icon ? (
    <span className={styles.icon}>{icon}</span>
  ) : null;

  const isPacmanLight = ["secondary", "blurred", "link"].includes(
    variant || "primary",
  );
  const isPacmanSmall = size === "xs" || size === "xxs";

  const buttonClassName = cx(buttonVariants({ variant, size }), className, {
    [styles.hasIcon]: !!icon,
    [styles.gutterTop]: gutterTop,
    [styles.skeleton]: skeleton,
    [styles.maxContent]: skeleton && variant === "link",
  });

  return skeleton ? (
    <Skeleton className={buttonClassName}>{children}</Skeleton>
  ) : (
    <button
      className={buttonClassName}
      ref={ref}
      disabled={loading || props.disabled}
      {...props}
      data-variant={variant || "primary"}
      data-size={size || "default"}
      type={props.type || "button"}
    >
      {loading && (
        <Pacman
          size={isPacmanSmall ? "sm" : "default"}
          tone={isPacmanLight ? "light" : "dark"}
        />
      )}
      {!loading && (
        <>
          {iconComponent}
          {children}
        </>
      )}
    </button>
  );
};
Button.displayName = "Button";

export { Button, buttonVariants };
